import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Archive, Clock, MessageCircle, PackageCheck, Plus, Search, X } from 'lucide-react';
import api, { getApiErrorMessage } from '../../services/api';
import { formatDateTimeSaoPaulo } from '../../utils/formatters';

const STATUS_OPTIONS = [
  { value: 'todos', label: 'Todos' },
  { value: 'aguardando_entrega', label: 'Aguardando entrega' },
  { value: 'recebido_portaria', label: 'Recebido na portaria' },
  { value: 'cancelado', label: 'Cancelado' },
];

const STATUS_LABELS = {
  aguardando_entrega: 'Aguardando entrega',
  recebido_portaria: 'Recebido na portaria',
  retirado_morador: 'Retirado pelo morador',
  cancelado: 'Cancelado',
};

const STATUS_STYLES = {
  aguardando_entrega: 'bg-amber-50 text-amber-700 border-amber-200',
  recebido_portaria: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  retirado_morador: 'bg-slate-100 text-slate-700 border-slate-200',
  cancelado: 'bg-red-50 text-red-700 border-red-200',
};

function statusLabel(status) {
  return STATUS_LABELS[status] || status;
}

const emptyForm = { morador_id: '', codigo_retirada: '', observacao: '' };

export default function DeliveryPage() {
  const [pedidos, setPedidos] = useState([]);
  const [moradores, setMoradores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('todos');
  const [mostrarRetirados, setMostrarRetirados] = useState(false);
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [moradorSearch, setMoradorSearch] = useState('');

  const fetchPedidos = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const params = {};
      if (searchTerm.trim()) params.busca = searchTerm.trim();
      if (mostrarRetirados) {
        params.status = 'retirado_morador';
      } else if (status !== 'todos') {
        params.status = status;
      }
      if (inicio) params.inicio = `${inicio}T00:00:00`;
      if (fim) params.fim = `${fim}T23:59:59`;
      const response = await api.get('/delivery/pedidos', { params });
      setPedidos(response.data || []);
    } catch (error) {
      alert(getApiErrorMessage(error, 'Nao foi possivel carregar os pedidos de delivery.'));
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [fim, inicio, mostrarRetirados, searchTerm, status]);

  const fetchMoradores = useCallback(async () => {
    try {
      const response = await api.get('/delivery/moradores');
      setMoradores(response.data || []);
    } catch (error) {
      setMoradores([]);
    }
  }, []);

  useEffect(() => {
    fetchMoradores();
  }, [fetchMoradores]);

  useEffect(() => {
    const timeout = window.setTimeout(() => fetchPedidos(true), 250);
    return () => window.clearTimeout(timeout);
  }, [fetchPedidos]);

  useEffect(() => {
    const interval = window.setInterval(() => fetchPedidos(false), 30000);
    return () => window.clearInterval(interval);
  }, [fetchPedidos]);

  const moradoresFiltrados = moradores.filter((morador) => {
    const termo = moradorSearch.trim().toLowerCase();
    if (!termo) return true;
    return `${morador.nome || ''} ${morador.unidade || ''} ${morador.contato || ''}`.toLowerCase().includes(termo);
  }).slice(0, 8);

  const selectedMorador = moradores.find((morador) => String(morador.id) === String(form.morador_id));

  const runAction = async (pedido, action, label) => {
    if (!window.confirm(`Confirmar: ${label}?`)) return;
    setActionLoadingId(`${action}-${pedido.id}`);
    try {
      const response = await api.patch(`/delivery/pedidos/${pedido.id}/${action}`);
      const atualizado = response.data?.pedido;
      if (atualizado) {
        setPedidos((current) => current.map((item) => (item.id === atualizado.id ? atualizado : item)));
      }
      alert(response.data?.message || 'Pedido atualizado.');
    } catch (error) {
      alert(getApiErrorMessage(error, 'Nao foi possivel atualizar o pedido.'));
      fetchPedidos(false);
    } finally {
      setActionLoadingId(null);
    }
  };

  const reenviarNotificacao = async (pedido) => {
    setActionLoadingId(`reenviar-${pedido.id}`);
    try {
      const response = await api.post(`/delivery/pedidos/${pedido.id}/reenviar-notificacao`);
      const atualizado = response.data?.pedido;
      if (atualizado) {
        setPedidos((current) => current.map((item) => (item.id === atualizado.id ? atualizado : item)));
      }
      alert(response.data?.message || 'Notificacao reenviada.');
    } catch (error) {
      alert(getApiErrorMessage(error, 'Nao foi possivel reenviar a notificacao.'));
      fetchPedidos(false);
    } finally {
      setActionLoadingId(null);
    }
  };

  const submitNovoPedido = async (event) => {
    event.preventDefault();
    if (!form.morador_id || !form.codigo_retirada.trim()) {
      alert('Selecione o morador e informe o codigo.');
      return;
    }
    try {
      const response = await api.post('/delivery/pedidos', {
        morador_id: Number(form.morador_id),
        codigo_retirada: form.codigo_retirada.trim(),
        observacao: form.observacao.trim() || null,
      });
      setPedidos((current) => [response.data, ...current]);
      setForm(emptyForm);
      setMoradorSearch('');
      setModalOpen(false);
      setMostrarRetirados(false);
      alert('Pedido registrado.');
    } catch (error) {
      alert(getApiErrorMessage(error, 'Nao foi possivel criar o pedido.'));
    }
  };

  const renderNotificacao = (pedido) => {
    if (pedido.notificacao_status === 'enviada') {
      return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700"><MessageCircle size={13} /> Enviada</span>;
    }
    if (pedido.notificacao_status === 'falhou') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-xs font-bold text-red-700" title={pedido.notificacao_erro || ''}>
          <AlertTriangle size={13} /> Pendente
        </span>
      );
    }
    return <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-500"><Clock size={13} /> Pendente</span>;
  };

  const actionDisabled = (pedido, action) => actionLoadingId === `${action}-${pedido.id}`;
  const pedidosVisiveis = pedidos.filter((pedido) => {
    if (mostrarRetirados) return pedido.status === 'retirado_morador';
    if (status !== 'todos') return pedido.status === status;
    return ['aguardando_entrega', 'recebido_portaria'].includes(pedido.status);
  });

  return (
    <div className="flex min-h-full flex-col gap-6 px-1 sm:px-2 lg:px-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-800">
            <span className="rounded-lg bg-condo-dark p-2"><PackageCheck className="text-white" size={20} /></span>
            Delivery
          </h1>
          <p className="text-sm text-gray-500">Acompanhe codigos enviados pelos moradores no WhatsApp</p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <button type="button" onClick={() => setModalOpen(true)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-slate-800">
            <Plus size={18} /> Novo pedido
          </button>
          <button
            type="button"
            onClick={() => setMostrarRetirados(false)}
            className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold shadow-sm transition ${
              !mostrarRetirados
                ? 'border border-slate-900 bg-slate-900 text-white hover:bg-slate-800'
                : 'border border-gray-200 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            <PackageCheck size={18} /> Pedidos ativos
          </button>
          <button
            type="button"
            onClick={() => setMostrarRetirados(true)}
            className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold shadow-sm transition ${
              mostrarRetirados
                ? 'border border-slate-900 bg-slate-900 text-white hover:bg-slate-800'
                : 'border border-gray-200 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Archive size={18} /> Retirados
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm lg:grid-cols-[1fr_220px_160px_160px]">
        <label className="relative block">
          <Search className="absolute left-3 top-3 text-gray-400" size={18} />
          <input className="w-full rounded-xl border border-gray-200 bg-white py-3 pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-condo-teal" placeholder="Pesquisar por codigo, nome, telefone ou unidade" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
        </label>
        <select className="rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-condo-teal disabled:bg-gray-50 disabled:text-slate-400" value={status} onChange={(event) => setStatus(event.target.value)} disabled={mostrarRetirados}>
          {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <input type="date" className="rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-condo-teal" value={inicio} onChange={(event) => setInicio(event.target.value)} />
        <input type="date" className="rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-condo-teal" value={fim} onChange={(event) => setFim(event.target.value)} />
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {pedidosVisiveis.map((pedido) => {
          const podeReceber = pedido.status === 'aguardando_entrega';
          const podeRetirar = ['aguardando_entrega', 'recebido_portaria'].includes(pedido.status);
          const podeCancelar = !['retirado_morador', 'cancelado'].includes(pedido.status);

          return (
            <div
              key={pedido.id}
              className={`flex min-h-[320px] flex-col justify-between rounded-3xl border bg-white p-6 text-center shadow-sm transition hover:shadow-md ${
                pedido.status === 'aguardando_entrega' ? 'border-amber-200 ring-2 ring-amber-100' : 'border-gray-100'
              }`}
            >
              <div className="flex items-center justify-between gap-3 text-left">
                <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-bold ${STATUS_STYLES[pedido.status] || 'border-slate-200 bg-slate-100 text-slate-700'}`}>
                  {statusLabel(pedido.status)}
                </span>
                {renderNotificacao(pedido)}
              </div>

              <div className="flex flex-1 flex-col items-center justify-center py-6">
                <h2 className="text-2xl font-black uppercase tracking-normal text-slate-900">NOVO PEDIDO</h2>
                <div className="mt-5 flex min-h-[96px] w-full items-center justify-center rounded-2xl bg-slate-900 px-4 py-5 text-center text-4xl font-black tracking-normal text-white shadow-inner">
                  <span className="break-all leading-tight">{pedido.codigo_retirada}</span>
                </div>
                <p className="mt-5 max-w-full break-words text-lg font-black text-slate-800">{pedido.nome_morador || '-'}</p>
                <p className="mt-1 text-sm font-bold uppercase tracking-wide text-slate-500">Unidade {pedido.unidade || '-'}</p>
                <div className="mt-3 space-y-1 text-xs font-semibold text-slate-400">
                  <p>{formatDateTimeSaoPaulo(pedido.criado_em)}</p>
                  {pedido.usuario_portaria && <p>Portaria: {pedido.usuario_portaria}</p>}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <ActionButton disabled={!podeReceber || actionDisabled(pedido, 'receber')} onClick={() => runAction(pedido, 'receber', 'pedido recebido')}>Recebido</ActionButton>
                <ActionButton disabled={!podeRetirar || actionDisabled(pedido, 'retirar')} onClick={() => runAction(pedido, 'retirar', 'pedido retirado')}>Retirado</ActionButton>
                <ActionButton danger disabled={!podeCancelar || actionDisabled(pedido, 'cancelar')} onClick={() => runAction(pedido, 'cancelar', 'cancelar pedido')}>Cancelar</ActionButton>
              </div>

              {pedido.status === 'recebido_portaria' && pedido.notificacao_status === 'falhou' && (
                <button
                  type="button"
                  disabled={actionLoadingId === `reenviar-${pedido.id}`}
                  onClick={() => reenviarNotificacao(pedido)}
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <MessageCircle size={14} /> Reenviar notificacao
                </button>
              )}
            </div>
          );
        })}

        {!loading && pedidosVisiveis.length === 0 && (
          <div className="col-span-full rounded-3xl border border-gray-100 bg-white px-6 py-20 text-center italic text-gray-400 shadow-sm">
            {mostrarRetirados ? 'Nenhum pedido retirado encontrado.' : 'Nenhum pedido de delivery encontrado.'}
          </div>
        )}

        {loading && (
          <div className="col-span-full rounded-3xl border border-gray-100 bg-white px-6 py-20 text-center italic text-gray-400 shadow-sm">
            Carregando pedidos...
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/60 p-4">
          <form onSubmit={submitNovoPedido} className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl">
            <div className="mb-5 flex items-center justify-between gap-4">
              <h2 className="text-lg font-black text-slate-800">Novo pedido</h2>
              <button type="button" onClick={() => setModalOpen(false)} className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Morador</label>
                <input className="w-full rounded-xl border border-gray-200 px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-condo-teal" placeholder="Buscar por nome, unidade ou telefone" value={moradorSearch} onChange={(event) => setMoradorSearch(event.target.value)} />
                <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-gray-100">
                  {moradoresFiltrados.map((morador) => (
                    <button key={morador.id} type="button" onClick={() => { setForm((current) => ({ ...current, morador_id: morador.id })); setMoradorSearch(`${morador.nome} ${morador.unidade ? `(${morador.unidade})` : ''}`); }} className={`block w-full px-3 py-2 text-left text-sm transition hover:bg-teal-50 ${String(form.morador_id) === String(morador.id) ? 'bg-teal-50 font-bold text-condo-dark' : 'text-slate-600'}`}>
                      {morador.nome} <span className="text-xs text-slate-400">{morador.unidade || '-'}</span>
                    </button>
                  ))}
                </div>
                {selectedMorador && <p className="mt-2 text-xs font-bold text-condo-dark">Selecionado: {selectedMorador.nome} {selectedMorador.unidade ? `(${selectedMorador.unidade})` : ''}</p>}
              </div>
              <label className="block">
                <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Codigo</span>
                <input className="w-full rounded-xl border border-gray-200 px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-condo-teal" value={form.codigo_retirada} onChange={(event) => setForm((current) => ({ ...current, codigo_retirada: event.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Observacao</span>
                <textarea className="min-h-24 w-full rounded-xl border border-gray-200 px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-condo-teal" value={form.observacao} onChange={(event) => setForm((current) => ({ ...current, observacao: event.target.value }))} />
              </label>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setModalOpen(false)} className="rounded-xl border border-gray-200 px-5 py-3 text-sm font-bold text-slate-600 transition hover:bg-slate-50">Cancelar</button>
              <button type="submit" className="rounded-xl bg-slate-900 px-5 py-3 text-sm font-bold text-white transition hover:bg-slate-800">Salvar pedido</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function ActionButton({ children, onClick, disabled, danger = false }) {
  const base = danger ? 'bg-red-50 text-red-700 hover:bg-red-600 hover:text-white' : 'bg-slate-900 text-white hover:bg-slate-800';
  return (
    <button type="button" disabled={disabled} onClick={onClick} className={`rounded-lg px-3 py-2 text-xs font-bold transition disabled:cursor-not-allowed disabled:opacity-50 ${base}`}>
      {children}
    </button>
  );
}
